/**
 * Current interaction state machine - replaces useCurrentInteractionStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { Interaction, InteractionMessage } from '../../../interaction/model/types'

export interface CurrentInteractionContext {
  interaction: Interaction | null
  messages: InteractionMessage[]
  isLoading: boolean
  error: string | null
}

export type CurrentInteractionEvent =
  | { type: 'SET_INTERACTION'; interaction: Interaction | null }
  | { type: 'SET_MESSAGES'; messages: InteractionMessage[] }
  | { type: 'ADD_MESSAGE'; message: InteractionMessage }
  | { type: 'UPDATE_MESSAGE'; id: string; updates: Partial<InteractionMessage> }
  | { type: 'REMOVE_MESSAGE'; id: string }
  | { type: 'SORT_MESSAGES' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as CurrentInteractionContext,
    events: {} as CurrentInteractionEvent,
  },
})

function sortMessages(messages: InteractionMessage[]) {
  return [...messages].sort((a, b) => Number(a.id) - Number(b.id))
}

export const currentInteractionMachine = machineSetup.createMachine({
  id: 'currentInteraction',
  context: {
    interaction: null,
    messages: [],
    isLoading: false,
    error: null,
  },
  on: {
    SET_INTERACTION: {
      actions: assign({
        interaction: ({ event }) => (event.type === 'SET_INTERACTION' ? event.interaction : null),
      }),
    },
    SET_MESSAGES: {
      actions: assign({
        messages: ({ event }) => (event.type === 'SET_MESSAGES' ? event.messages : []),
      }),
    },
    ADD_MESSAGE: {
      actions: assign({
        messages: ({ context, event }) => {
          if (event.type !== 'ADD_MESSAGE') return context.messages
          const existing = context.messages.findIndex((m) => m.id === event.message.id)
          let next: InteractionMessage[]
          if (existing !== -1) {
            next = [...context.messages]
            next[existing] = event.message
          } else {
            next = [...context.messages, event.message]
          }
          return sortMessages(next)
        },
      }),
    },
    UPDATE_MESSAGE: {
      actions: assign({
        messages: ({ context, event }) => {
          if (event.type !== 'UPDATE_MESSAGE') return context.messages
          return context.messages.map((m) => (m.id === event.id ? { ...m, ...event.updates } : m))
        },
      }),
    },
    REMOVE_MESSAGE: {
      actions: assign({
        messages: ({ context, event }) => {
          if (event.type !== 'REMOVE_MESSAGE') return context.messages
          return context.messages.filter((m) => m.id !== event.id)
        },
      }),
    },
    SORT_MESSAGES: {
      actions: assign({
        messages: ({ context }) => sortMessages(context.messages),
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    SET_ERROR: {
      actions: assign({
        error: ({ event }) => (event.type === 'SET_ERROR' ? event.error : null),
      }),
    },
    CLEAR: {
      actions: assign({
        interaction: null,
        messages: [],
        isLoading: false,
        error: null,
      }),
    },
  },
})
